import React from 'react';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useControlDisabled } from './ControlDisabledContext';
import { joinClasses } from './fieldClassSlots';

export interface OASISDateRenderProps {
  /** YYYY-MM-DD string suitable for <input type="date"> */
  value: string;
  /** Call with a YYYY-MM-DD string; stored internally as YYYYMMDD */
  onChange: (v: string) => void;
  label: string;
}

export interface OASISDateProps {
  qid: string;
  className?: string;
  hideLabel?: boolean;
  innerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  children?: (props: OASISDateRenderProps) => React.ReactNode;
}

function toInputValue(raw: unknown): string {
  if (!raw) return '';
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  const s = String(raw);
  if (/^\d{8}$/.test(s))
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  return s;
}

function fromInputValue(s: string): string {
  return s.replace(/-/g, '');
}

export function OASISDate({
  qid,
  className,
  hideLabel,
  innerClassName,
  labelClassName,
  inputClassName,
  children,
}: OASISDateProps) {
  const inheritedDisabled = useControlDisabled();
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);

  const value = toInputValue(answers[qid]);
  const onChange = (v: string) => onAnswerChange(qid, fromInputValue(v));
  const label = question?.question ?? question?.description ?? qid;

  return (
    <div data-qid={qid} className={className}>
      {children ? (
        children({ value, onChange, label })
      ) : (
        <div className={innerClassName ?? 'space-y-2'}>
          {!hideLabel && (
            <label
              className={joinClasses(
                'block text-sm font-semibold text-foreground',
                labelClassName
              )}
            >
              {label}
            </label>
          )}
          <input
            type='date'
            disabled={inheritedDisabled}
            value={value}
            onChange={e => onChange(e.target.value)}
            className={`w-full rounded-md border px-3 py-2 text-sm${inputClassName ? ` ${inputClassName}` : ''}`}
          />
        </div>
      )}
    </div>
  );
}
